/**
 * "Add to calendar" (Phase 4) — reviewer-side convenience only. No Graph, no tenant admin:
 * just a plain Outlook web deep link (opened in a new tab) plus a downloadable .ics that
 * works in any calendar app. Both pre-fill the property address + valuer name and
 * deliberately omit a start/end time so the reviewer sets it manually.
 *
 * The Outlook deep link format is documented but finicky across tenants (login-loop
 * quirks have been reported) — that's why the .ics exists as a real fallback, not an
 * afterthought. If the deep link proves unreliable against this firm's tenant, swap which
 * one AddToCalendar treats as primary; the rest of this module is unaffected.
 */

const FALLBACK_ADDRESS = 'Address not recorded';

function eventText(addressRaw: string | null, valuerName: string) {
  const address = addressRaw?.trim() || FALLBACK_ADDRESS;
  const subject = `Valuation review: ${valuerName} — ${address}`;
  const body = `Valuer: ${valuerName}\nProperty: ${address}`;
  return { address, subject, body };
}

/** Outlook web compose deep link. No startdt/enddt — Outlook leaves the time blank for manual entry. */
export function buildCalendarLink(addressRaw: string | null, valuerName: string): string {
  const { address, subject, body } = eventText(addressRaw, valuerName);
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject,
    location: address,
    body,
  });
  return `https://outlook.office.com/calendar/deeplink/compose?${params.toString()}`;
}

function icsEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
}

function todayStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

function nowStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

/**
 * .ics fallback for the same event. Uses an all-day placeholder on today's date (DTSTART
 * with VALUE=DATE, no time component) rather than omitting DTSTART entirely — most
 * calendar apps, including Outlook desktop, require a start date on a VEVENT. The reviewer
 * drags it to the right day/time manually.
 */
export function buildCalendarIcs(reportId: string, addressRaw: string | null, valuerName: string): { filename: string; content: string } {
  const { address, subject, body } = eventText(addressRaw, valuerName);
  const start = todayStamp();
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Valuation QA Reviewer//Add to calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${reportId}@valuation-qa`,
    `DTSTAMP:${nowStamp()}`,
    `DTSTART;VALUE=DATE:${start}`,
    `SUMMARY:${icsEscape(subject)}`,
    `LOCATION:${icsEscape(address)}`,
    `DESCRIPTION:${icsEscape(body)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return { filename: `${address.replace(/[^\w\- ]/g, '').trim() || 'valuation-review'}.ics`, content: lines.join('\r\n') };
}
